// ── BCH Live — Discovery Homepage ─────────────────────────────────
//
// Sections: Featured · Live Now · Trending · Categories · Upcoming ·
// Recently Ended · Recommended Creators, plus search.
//
// Ranking comes from useStreamDiscovery, which blends chat, BCH tips,
// reactions, shares, retention and recency — deliberately never viewer
// count alone.

import React, { useState, useMemo, useDeferredValue } from 'react';
import { Link } from 'react-router-dom';
import {
  Radio, RefreshCw, Plus, Search, X, VideoOff, TrendingUp, Clock,
  History, Sparkles, Coins, Users, Flame, Code2, Gamepad2, Music,
  Camera, GraduationCap, Newspaper, Store, Mic2, Palette, User, ArrowRight,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useStreamDiscovery } from '../hooks/useStreamDiscovery';
import { STREAM_CATEGORIES, type LiveStream } from '../lib/liveStream';
import {
  StreamCard, FeaturedStream, StreamGridSkeleton, FeaturedSkeleton,
  streamHref,
} from '../components/live/StreamCard';
import FollowButton from '../components/FollowButton';
import VerifiedBadge from '../components/VerifiedBadge';
import { useNip05Verified } from '../hooks/useNip05Verified';

// Category icons are referenced by name in the taxonomy; map them here so
// the lib stays free of JSX/component imports.
const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Coins, TrendingUp, Code2, Gamepad2, Music, Camera,
  GraduationCap, Newspaper, Store, Mic2, Palette, Radio,
};

// ── Section heading ───────────────────────────────────────────────
function SectionHeader({
  icon: Icon, title, count, accent = 'text-foreground', action,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count?: number;
  accent?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className={`flex items-center gap-2 text-base font-extrabold ${accent}`}>
        <Icon className="h-4 w-4" />
        {title}
        {typeof count === 'number' && count > 0 && (
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-bold text-muted-foreground">
            {count}
          </span>
        )}
      </h2>
      {action}
    </div>
  );
}

// ── Recommended creator row ───────────────────────────────────────
function RecommendedCreator({ stream }: { stream: LiveStream }) {
  const { profile } = useNostrProfile(stream.hostPubkey);
  const nip05Verified = useNip05Verified(profile?.nip05, stream.hostPubkey);
  const href = streamHref(stream, profile?.nip05, nip05Verified);
  const name = profile?.display_name || profile?.name || stream.hostPubkey.slice(0, 8);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:border-primary/30">
      <Link to={href} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" loading="lazy" className="h-11 w-11 rounded-full object-cover ring-2 ring-border" />
        ) : (
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 ring-2 ring-border">
            <User className="h-5 w-5 text-primary/60" />
          </div>
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <Link
          to={href}
          className="flex items-center gap-1.5 text-sm font-bold hover:text-primary"
        >
          <span className="truncate">{name}</span>
          <VerifiedBadge nip05={profile?.nip05} pubkey={stream.hostPubkey} size="xs" />
          {stream.isLive && <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-red-500" />}
        </Link>
        <p className="truncate text-[11px] text-muted-foreground">
          {stream.category?.label || (stream.isLive ? 'Live now' : 'Streamer')}
        </p>
      </div>
      <div className="shrink-0">
        <FollowButton pubkey={stream.hostPubkey} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════════════

export default function BCHLivePage() {
  const { user } = useAppContext();
  const {
    live, upcoming, ended, featured, trending,
    loading, refetch, signalsFor, categoryCounts, totalTippedUsd,
  } = useStreamDiscovery();

  const [rawQuery, setRawQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // Deferred so typing stays responsive on large directories.
  const query = useDeferredValue(rawQuery);

  const isFiltering = query.trim().length > 0 || !!activeCategory;

  // ── Search + category filtering over every known stream ──
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = [...live, ...upcoming, ...ended];
    return pool.filter(s => {
      if (activeCategory && s.category?.id !== activeCategory) return false;
      if (!q) return true;
      return (
        s.title.toLowerCase().includes(q)
        || s.summary.toLowerCase().includes(q)
        || s.topics.some(t => t.toLowerCase().includes(q))
        || (s.category?.label.toLowerCase().includes(q) ?? false)
      );
    });
  }, [query, activeCategory, live, upcoming, ended]);

  // Recommended = live creators not already in the featured slot,
  // ordered by engagement score.
  const recommended = useMemo(
    () => [...live, ...upcoming].filter(s => s !== featured).slice(0, 6),
    [live, upcoming, featured],
  );

  return (
    <div className="pb-10">
      {/* ── Header ── */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <h1 className="flex min-w-0 items-center gap-2 text-lg font-extrabold">
            <Radio className="h-5 w-5 shrink-0 text-red-500" />
            <span className="truncate">BCH Live</span>
            {live.length > 0 && (
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-bold text-red-500">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                {live.length}
              </span>
            )}
          </h1>

          <div className="flex shrink-0 items-center gap-1.5">
            <button
              onClick={refetch}
              title="Refresh"
              aria-label="Refresh streams"
              className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            {user && (
              <Link
                to="/creator/live"
                className="flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-1.5 text-sm font-bold text-white transition-colors hover:bg-red-600"
              >
                <Plus className="h-4 w-4" /> Go Live
              </Link>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={rawQuery}
              onChange={e => setRawQuery(e.target.value)}
              placeholder="Search streams, categories, tags…"
              aria-label="Search streams"
              className="w-full rounded-xl border border-border bg-muted/50 py-2 pl-9 pr-9 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {rawQuery && (
              <button
                onClick={() => setRawQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-8 px-4 pt-4">
        {/* ── BCH stat strip ── */}
        {totalTippedUsd > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-3 py-2">
            <Coins className="h-4 w-4 shrink-0 text-emerald-500" />
            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              ${totalTippedUsd.toFixed(2)} in Bitcoin Cash tipped to streamers recently
            </p>
          </div>
        )}

        {/* ── Loading ── */}
        {loading ? (
          <>
            <FeaturedSkeleton />
            <div>
              <div className="mb-3 h-4 w-28 animate-pulse rounded bg-muted" />
              <StreamGridSkeleton count={8} />
            </div>
          </>
        ) : isFiltering ? (
          /* ── Filtered results ── */
          <section>
            <SectionHeader
              icon={Search}
              title={activeCategory
                ? STREAM_CATEGORIES.find(c => c.id === activeCategory)?.label || 'Results'
                : 'Search results'}
              count={filtered.length}
              action={
                <button
                  onClick={() => { setRawQuery(''); setActiveCategory(null); }}
                  className="flex items-center gap-1 rounded-lg bg-muted px-2 py-1 text-[11px] font-bold text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" /> Clear
                </button>
              }
            />
            {filtered.length === 0 ? (
              <EmptyState
                icon={Search}
                title="Nothing matches that"
                body="Try a different search term, or clear the filters to browse everything."
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map(s => (
                  <StreamCard key={s.aTag} stream={s} signals={signalsFor(s.aTag)} />
                ))}
              </div>
            )}
          </section>
        ) : (
          <>
            {/* ── Featured ── */}
            {featured && (
              <section className="live-reveal">
                <FeaturedStream stream={featured} signals={signalsFor(featured.aTag)} />
              </section>
            )}

            {/* ── 24/7 / always-on callout ── */}
            <section className="flex items-start gap-3 rounded-2xl border-2 border-primary/40 bg-primary/5 p-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-extrabold">Want a more reliable stream?</h3>
                  <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary-foreground">
                    Recommended
                  </span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Tired of your stream dropping, or want to run 24/7? Use dedicated software like
                  Owncast that broadcasts its own live signal — it stays live without keeping a
                  browser tab open.
                </p>
                <Link
                  to="/creator/live?guide=1"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                >
                  See how to set it up <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </section>

            {/* ── Live Now ── */}
            {live.length > 0 && (
              <section className="live-reveal">
                <SectionHeader icon={Flame} title="Live Now" count={live.length} accent="text-red-500" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {live.map(s => (
                    <StreamCard key={s.aTag} stream={s} signals={signalsFor(s.aTag)} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Trending ── */}
            {trending.length > 0 && (
              <section className="live-reveal">
                <SectionHeader
                  icon={TrendingUp}
                  title="Trending"
                  accent="text-emerald-500"
                  action={
                    <span className="text-[10px] font-medium text-muted-foreground">
                      Ranked by chat, tips &amp; reactions
                    </span>
                  }
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {trending.map(s => (
                    <StreamCard key={s.aTag} stream={s} signals={signalsFor(s.aTag)} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Categories ── */}
            <section>
              <SectionHeader icon={Sparkles} title="Browse Categories" />
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {STREAM_CATEGORIES.map(cat => {
                  const Icon = CATEGORY_ICONS[cat.icon] || Radio;
                  const count = categoryCounts.get(cat.id) || 0;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className="group relative overflow-hidden rounded-xl border border-border p-3 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} opacity-10 transition-opacity group-hover:opacity-20`}
                      />
                      <div className="relative flex items-center gap-2">
                        <Icon className="h-4 w-4 shrink-0 text-foreground/70" />
                        <span className="truncate text-xs font-bold">{cat.label}</span>
                      </div>
                      <p className="relative mt-1 text-[10px] font-semibold text-muted-foreground">
                        {count > 0 ? `${count} stream${count === 1 ? '' : 's'}` : 'No streams'}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ── Upcoming ── */}
            {upcoming.length > 0 && (
              <section className="live-reveal">
                <SectionHeader icon={Clock} title="Upcoming Streams" count={upcoming.length} accent="text-primary" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {upcoming.map(s => (
                    <StreamCard key={s.aTag} stream={s} signals={signalsFor(s.aTag)} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Recommended creators ── */}
            {recommended.length > 0 && (
              <section>
                <SectionHeader icon={Users} title="Recommended Creators" />
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                  {recommended.map(s => (
                    <RecommendedCreator key={s.aTag} stream={s} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Recently ended ── */}
            {ended.length > 0 && (
              <section>
                <SectionHeader icon={History} title="Recently Ended" count={ended.length} accent="text-muted-foreground" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {ended.map(s => (
                    <StreamCard key={s.aTag} stream={s} signals={signalsFor(s.aTag)} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Nothing at all ── */}
            {live.length === 0 && upcoming.length === 0 && ended.length === 0 && (
              <EmptyState
                icon={VideoOff}
                title="No streams right now"
                body="When someone goes live, they'll show up here automatically."
                action={
                  user ? (
                    <Link
                      to="/creator/live"
                      className="mt-1 inline-flex items-center gap-2 rounded-xl bg-red-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-600"
                    >
                      <Plus className="h-4 w-4" /> Start streaming
                    </Link>
                  ) : null
                }
              />
            )}

            {/* ── How it works ── */}
            <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-extrabold">Stream from anywhere. Watch here. Get paid in Bitcoin Cash.</h3>
              <p className="text-xs leading-relaxed text-muted-foreground">
                BCH Live works with any streaming platform — paste your stream link and go live. We
                embed the player and layer Nostr chat, Super Chats and instant BCH tipping on top.
                No middleman, no payout delays, no chargebacks.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                {[
                  { name: 'Twitch', color: '#9146FF' },
                  { name: 'YouTube', color: '#FF0000' },
                  { name: 'Kick', color: '#53FC18' },
                  { name: 'Rumble', color: '#85C742' },
                  { name: 'Shosho', color: '#6366F1' },
                  { name: 'HLS / Owncast', color: '#0AC18E' },
                ].map(p => (
                  <span
                    key={p.name}
                    className="rounded-full px-2.5 py-1 text-[10px] font-bold text-white"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.name}
                  </span>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────
function EmptyState({
  icon: Icon, title, body, action,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-border py-16 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
        <Icon className="h-8 w-8 text-muted-foreground/40" />
      </div>
      <h2 className="text-base font-bold text-foreground">{title}</h2>
      <p className="mx-auto mt-1.5 max-w-sm px-6 text-sm text-muted-foreground">{body}</p>
      {action}
    </div>
  );
}
